<template>
  <div
    v-if="loadingDetail"
    class="eu-pa_6 eu-align_center"
  >
    <emd-loader :type="'_invert'"></emd-loader>
  </div>
  <div v-else>
    <div class="el-section">
      <div class="el-grid-list">
        <div class="emd-container">
          <div
            class="emd-status-bar"
            v-bind:class="statusClass(subscription.status)"
          ></div>
          <div class="emd-container__content">
            <div class="el-chunk">
              <h4 class="emd-text _color_subtle _type_label _size_tiny">
                {{ $t('subscription.info.status') }}
              </h4>
            </div>
            <span
              class="emd-badge"
              v-bind:class="statusClass(subscription.status)"
              >{{ subscription.status }}</span
            >
          </div>
        </div>
        <div class="emd-container">
          <div class="emd-container__content">
            <div class="el-chunk">
              <h4 class="emd-text _color_subtle _type_label _size_tiny">
                {{ $t('subscription.info.interval') }}
              </h4>
            </div>
            <p class="emd-text _size_section">
              {{ $t('subscription.info.interval-each') }}
              {{ subscription.interval_count }}
              {{
                pluralIntervals(
                  subscription.interval_count,
                  subscription.interval
                )
              }}
            </p>
          </div>
        </div>
        <div class="emd-container">
          <div class="emd-container__content">
            <div class="el-chunk">
              <h4 class="emd-text _color_subtle _type_label _size_tiny">
                {{ $t('subscription.info.payment-method') }}
              </h4>
            </div>
            <p class="emd-text _size_section">
              {{ subscription.payment_method }}
            </p>
          </div>
        </div>
        <div class="emd-container">
          <div class="emd-container__content">
            <div class="el-chunk">
              <h4 class="emd-text _color_subtle _type_label _size_tiny">
                {{ $t('subscription.info.start-at') }}
              </h4>
            </div>
            <p class="emd-text _size_section">
              {{ subscription.start_at | dateFormat('date') }}
            </p>
          </div>
        </div>
      </div>
    </div>
    <div class="el-section">
      <div class="emd-container _highlight_header">
        <div class="emd-container__header">
          <h2 class="emd-text _size_section">
            {{ $t('subscription.info.title') }}
          </h2>

          <button
            v-if="
              showCancelButton &&
              permissionIsEnabled('subscription:cancel') &&
              componentIsEnabled('subscription', 'cancel')
            "
            class="emd-btn _color_danger _size_small"
            type="button"
            v-on:click="openModal"
          >
            <i class="uil uil-times-circle" />
            {{ $t('subscription.info.cancel-subscription') }}
          </button>
        </div>
        <div class="emd-container__content">
          <div class="emd-panel">
            <div class="emd-panel__section">
              <div class="emd-panel__block">
                <div class="el-chunk">
                  <h2 class="emd-text _color_default _type_caption">
                    {{ $t('subscription.info.general-data') }}
                  </h2>
                </div>
              </div>
              <div class="emd-panel__block">
                <div class="el-chunk">
                  <p class="emd-text _type_label _color_subtle _size_tiny">
                    ID
                  </p>
                  <span>{{ subscription.id || '-' }}</span>
                </div>
                <div class="el-chunk">
                  <p class="emd-text _type_label _color_subtle _size_tiny">
                    {{ $t('subscription.info.code') }}
                  </p>
                  <span>{{ subscription.code || '-' }}</span>
                </div>
                <div class="el-chunk">
                  <p class="emd-text _type_label _color_subtle _size_tiny">
                    {{ $t('subscription.info.billing-type') }}
                  </p>
                  <span>{{ subscription.billing_type || '-' }}</span>
                </div>
                <div class="el-chunk">
                  <p class="emd-text _type_label _color_subtle _size_tiny">
                    {{ $t('subscription.info.currency') }}
                  </p>
                  <span>{{ subscription.currency || '-' }}</span>
                </div>
                <div class="el-chunk">
                  <p class="emd-text _type_label _color_subtle _size_tiny">
                    {{ $t('subscription.info.next-billing') }}
                  </p>
                  <span>{{
                    subscription.next_billing_at | dateFormat('date')
                  }}</span>
                </div>
                <div class="el-chunk">
                  <p class="emd-text _type_label _color_subtle _size_tiny">
                    {{ $t('subscription.info.installments') }}
                  </p>
                  <span>{{ subscription.installments || '-' }}</span>
                </div>
                <div
                  class="el-chunk"
                  v-if="subscription.created_at"
                >
                  <p class="emd-text _type_label _color_subtle _size_tiny">
                    {{ $t('subscription.info.created-at') }}
                  </p>
                  <span>{{
                    subscription.created_at | dateFormat('dateAndHour')
                  }}</span>
                </div>
                <div
                  class="el-chunk"
                  v-if="subscription.updated_at"
                >
                  <p class="emd-text _type_label _color_subtle _size_tiny">
                    {{ $t('subscription.info.updated-at') }}
                  </p>
                  <span>{{
                    subscription.updated_at | dateFormat('dateAndHour')
                  }}</span>
                </div>
                <div
                  class="el-chunk"
                  v-if="
                    subscription.metadata &&
                    Object.entries(subscription.metadata).length
                  "
                >
                  <p class="emd-text _color_subtle _type_label _size_tiny">
                    {{ $t('subscription.info.more-info') }}
                  </p>
                  <pre>{{ subscription.metadata }}</pre>
                </div>
              </div>
            </div>
          </div>
          <div class="emd-panel">
            <div class="emd-panel__section">
              <div class="emd-panel__block">
                <div class="el-chunk">
                  <h2 class="emd-text _color_default _type_caption">
                    {{ $t('subscription.info.customer.title') }}
                  </h2>
                </div>
              </div>
              <div class="emd-panel__block">
                <div class="el-chunk">
                  <p class="emd-text _type_label _color_subtle _size_tiny">
                    {{ $t('subscription.info.customer.name') }}
                  </p>
                  <router-link
                    v-if="permissionIsEnabled('customer:get')"
                    :to="{
                      name: 'customersDetail',
                      params: { id: subscription.customer.id, tab: 'info' }
                    }"
                    class="emd-link"
                    >{{ subscription.customer.name || '-' }}</router-link
                  >
                  <p
                    v-else
                    class="emd-text"
                  >
                    {{ subscription.customer.name || '-' }}
                  </p>
                </div>
                <div class="el-chunk">
                  <p class="emd-text _type_label _color_subtle _size_tiny">
                    {{ $t('subscription.info.customer.email') }}
                  </p>
                  <span>{{ subscription.customer.email || '-' }}</span>
                </div>
                <div
                  class="el-chunk"
                  v-if="
                    subscription.customer.phones &&
                    subscription.customer.phones.home_phone
                  "
                >
                  <p class="emd-text _type_label _color_subtle _size_tiny">
                    {{ $t('subscription.info.customer.phone') }}
                  </p>
                  <span
                    >{{ subscription.customer.phones.home_phone.country_code }}
                    {{ subscription.customer.phones.home_phone.area_code }}
                    {{ subscription.customer.phones.home_phone.number }}</span
                  >
                </div>
                <div
                  class="el-chunk"
                  v-if="
                    subscription.customer.phones &&
                    subscription.customer.phones.mobile_phone
                  "
                >
                  <p class="emd-text _type_label _color_subtle _size_tiny">
                    {{ $t('subscription.info.customer.mobile-phone') }}
                  </p>
                  <span
                    >{{
                      subscription.customer.phones.mobile_phone.country_code
                    }}
                    {{ subscription.customer.phones.mobile_phone.area_code }}
                    {{ subscription.customer.phones.mobile_phone.number }}</span
                  >
                </div>
              </div>
            </div>
          </div>
          <div
            class="emd-panel"
            v-if="subscription.plan"
          >
            <div class="emd-panel__section">
              <div class="emd-panel__block">
                <div class="el-chunk">
                  <h2 class="emd-text _color_default _type_caption">
                    {{ $t('subscription.info.plan.title') }}
                  </h2>
                </div>
              </div>
              <div class="emd-panel__block">
                <div class="el-chunk">
                  <p class="emd-text _type_label _color_subtle _size_tiny">
                    {{ $t('subscription.info.plan.name') }}
                  </p>
                  <router-link
                    v-if="permissionIsEnabled('plan:get')"
                    :to="{
                      name: 'plansDetail',
                      params: { id: subscription.plan.id, tab: 'info' }
                    }"
                    class="emd-link"
                    >{{ subscription.plan.name || '-' }}</router-link
                  >
                  <p
                    v-else
                    class="emd-text"
                  >
                    {{ subscription.plan.name || '-' }}
                  </p>
                </div>
                <div class="el-chunk">
                  <p class="emd-text _type_label _color_subtle _size_tiny">
                    {{ $t('subscription.info.plan.interval') }}
                  </p>
                  <span
                    >{{ $t('subscription.info.plan.interval-each') }}
                    {{ subscription.plan.interval_count }}
                    {{
                      pluralIntervals(
                        subscription.plan.interval_count,
                        subscription.plan.interval
                      )
                    }}</span
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      class="el-section"
      v-if="subscription.items.length"
    >
      <div class="emd-container _highlight_header">
        <div class="emd-container__header">
          <h2 class="emd-text _size_section">
            {{ $t('subscription.info.items.title') }}
          </h2>
        </div>
        <div class="emd-table-responsive">
          <table class="emd-table">
            <thead>
              <tr>
                <th>{{ $t('subscription.info.items.name') }}</th>
                <th>{{ $t('subscription.info.items.status') }}</th>
                <th>{{ $t('subscription.info.items.quantity') }}</th>
                <th>{{ $t('subscription.info.items.scheme') }}</th>
                <th>{{ $t('subscription.info.items.amount') }}</th>
                <th>{{ $t('subscription.info.items.updated-at') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, index) in subscription.items"
                :key="index"
              >
                <td :data-label="$t('subscription.info.items.name')">
                  {{ item.name || '-' }}
                </td>
                <td :data-label="$t('subscription.info.items.status')">
                  {{ item.status || '-' }}
                </td>
                <td :data-label="$t('subscription.info.items.quantity')">
                  {{ item.quantity || '-' }}
                </td>
                <td :data-label="$t('subscription.info.items.scheme')">
                  {{ item.pricing_scheme.scheme_type || '-' }}
                </td>
                <td :data-label="$t('subscription.info.items.amount')">
                  <div v-if="item.pricing_scheme.price_brackets.length">
                    <p
                      class="eu-pb_2"
                      v-for="(price, index) in item.pricing_scheme
                        .price_brackets"
                      :key="index"
                    >
                      {{ $t('subscription.info.items.interval-from') }}
                      {{ price.start_quantity }}
                      {{ $t('subscription.info.items.interval-to') }}
                      {{ price.end_quantity }}
                      <i class="eu-mr_1 eu-ml_1 uil uil-arrow-right" />
                      <span class="emd-text _type_caption">{{
                        price.price
                      }}</span>
                    </p>
                    <p class="eu-pb_2">
                      {{ $t('subscription.info.items.interval-over') }}
                      {{
                        lastPrice(item.pricing_scheme.price_brackets)
                          .end_quantity
                      }}
                      <i class="eu-mr_1 eu-ml_1 uil uil-arrow-right" />
                      <span class="emd-text _type_caption">{{
                        lastPrice(item.pricing_scheme.price_brackets)
                          .overage_price
                      }}</span>
                    </p>
                  </div>
                  <div v-else>
                    {{ item.pricing_scheme.price || '-' }}
                  </div>
                </td>
                <td :data-label="$t('subscription.info.items.updated-at')">
                  {{ item.updated_at | dateFormat('dateAndHour') }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <div
      class="el-section"
      v-if="subscription.setup"
    >
      <div class="emd-container _highlight_header">
        <div class="emd-container__header">
          <h2 class="emd-text _size_section">
            {{ $t('subscription.info.setup.title') }}
          </h2>
        </div>
        <div class="emd-container__content">
          <div class="emd-panel">
            <div class="emd-panel__section">
              <div class="emd-panel__block">
                <div class="el-chunk">
                  <h2 class="emd-text _color_default _type_caption">
                    {{ $t('subscription.info.general-data') }}
                  </h2>
                </div>
              </div>
              <div class="emd-panel__block">
                <div class="el-chunk">
                  <p class="emd-text _type_label _color_subtle _size_tiny">
                    {{ $t('subscription.info.setup.order-id') }}
                  </p>
                  <router-link
                    v-if="permissionIsEnabled('order:get')"
                    :to="{
                      name: 'ordersDetail',
                      params: { id: subscription.setup.id }
                    }"
                    class="emd-link"
                    >{{ subscription.setup.id || '-' }}</router-link
                  >
                  <p
                    v-else
                    class="emd-text"
                  >
                    {{ subscription.setup.id || '-' }}
                  </p>
                </div>
                <div class="el-chunk">
                  <p class="emd-text _type_label _color_subtle _size_tiny">
                    {{ $t('subscription.info.setup.amount') }}
                  </p>
                  <span>{{ subscription.setup.amount || '-' }}</span>
                </div>
                <div class="el-chunk">
                  <p class="emd-text _type_label _color_subtle _size_tiny">
                    {{ $t('subscription.info.setup.status') }}
                  </p>
                  <span
                    class="emd-badge"
                    v-bind:class="statusClass(subscription.setup.status)"
                    >{{ subscription.setup.status }}</span
                  >
                </div>
                <div class="el-chunk">
                  <p class="emd-text _type_label _color_subtle _size_tiny">
                    {{ $t('subscription.info.setup.description') }}
                  </p>
                  <span>{{ subscription.setup.description || '-' }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <log-links
      :entity="subscription"
      :title="$t('log-links.subscription')"
    ></log-links>

    <modal :open="modalOpenned">
      <template slot="content">
        <div class="emd-container _highlight_header _footer_end">
          <div class="emd-container__header">
            <h2 class="emd-text _size_section _color_secondary">
              {{ $t('subscription.info.modal.title') }}
            </h2>
            <button
              class="emd-btn _type_link jsModalClose"
              type="button"
              v-on:click="closeModal"
            >
              <i
                class="uil uil-times"
                transform="width-11"
              />
            </button>
          </div>
          <form
            @submit="cancelSubscription"
            name="cancelSubscription"
            novalidate="true"
          >
            <div class="emd-container__content">
              <emd-alert
                :variant="formError ? 'danger' : 'success'"
                v-if="showAlert"
                @cancel="showAlert = false"
                :title="
                  formError
                    ? $t('account.settings.alert.error-title')
                    : $t('account.settings.alert.success-title')
                "
              >
                <template slot="content">
                  <div v-if="formError">
                    <p
                      v-for="(error, index) in errors"
                      :key="index"
                    >
                      {{ error.message }}
                    </p>
                  </div>
                  <p v-else>{{ $t('account.settings.saved-success') }}</p>
                </template>
              </emd-alert>
              <div class="emd-panel">
                <h2 class="emd-text _size_section">
                  {{ $t('subscription.info.modal.cancel-detail') }}
                </h2>
              </div>
              <div class="emd-panel">
                <div class="emd-panel__section">
                  <div class="emd-form-group">
                    <jade-checkbox
                      id="cancelInvoices"
                      :value.sync="cancelInvoices"
                      :label="$t('subscription.info.modal.cancel-invoices')"
                    />
                  </div>
                </div>
                <password-confirmation
                  v-model="password"
                  :label="null"
                ></password-confirmation>
              </div>
            </div>
            <div class="emd-container__footer">
              <button
                class="emd-btn _color_primary _type_text"
                type="button"
                v-on:click="closeModal"
              >
                {{ $t('subscription.info.modal.button-cancel') }}
              </button>
              <button
                class="emd-btn _color_primary"
                type="submit"
                :disabled="!password"
              >
                <emd-loader
                  :type="''"
                  v-if="cancelLoading"
                ></emd-loader>
                <span v-else>{{
                  $t('subscription.info.modal.button-ok')
                }}</span>
              </button>
            </div>
          </form>
        </div>
      </template>
    </modal>
  </div>
</template>
<script>
import { EmdLoader, EmdAlert } from 'emerald-vue/src/components'
import Modal from '@/components/project/globals/Modal.vue'
import PasswordConfirmation from '@/components/project/molecules/PasswordConfirmation.vue'
import { statusClass, pluralIntervals } from '@/filters'
import LogLinks from '@/components/project/molecules/LogLinks/index.vue'
import { JadeCheckbox } from '@jade'

export default {
  name: 'SubscriptionInfo',
  components: {
    EmdLoader,
    Modal,
    PasswordConfirmation,
    EmdAlert,
    LogLinks,
    JadeCheckbox
  },
  data () {
    return {
      statusClass: statusClass,
      pluralIntervals: pluralIntervals,
      subscription: null,
      loadingDetail: true,
      modalOpenned: false,
      cancelInvoices: false,
      cancelLoading: false,
      password: '',
      errors: [],
      showAlert: false,
      formError: false
    }
  },
  methods: {
    getSubscription () {
      this.loadingDetail = true
      this.params.query = {
        is_test_account: this.isTestAccount,
        created_at: this.$route.query.created_at
      }
      this.$store.dispatch('GET_SUBSCRIPTION', this.params).then(res => {
        this.subscription = res
        this.loadingDetail = false
        this.showAlert = false
      })
    },
    openModal: function (type) {
      this.modalOpenned = true
    },
    closeModal: function () {
      this.modalOpenned = false
    },
    lastPrice: function (items) {
      return items[items.length - 1]
    },
    cancelSubscription: function (e) {
      e.preventDefault()
      this.cancelLoading = true
      this.params.body = {
        cancel_pending_invoices: this.cancelInvoices,
        password: window.btoa(this.password)
      }
      this.$store
        .dispatch('CANCEL_SUBSCRIPTION', this.params)
        .then(res => {
          this.closeModal()
          this.getSubscription()
          this.cancelLoading = false
          this.showAlert = true
          this.params.entityId = this.params.subscriptionId
          this.params.refresh = true
          this.$store.dispatch('GET_LAST_LOG', this.params)
        })
        .catch(err => {
          this.formError = true
          this.showAlert = true
          this.cancelLoading = false
          if (err && err.body) this.errors = err.body.errors
        })
    },
    componentIsEnabled (section, component) {
      return this.$store.getters.componentIsEnabled(section, component)
    },
    permissionIsEnabled (action) {
      return this.$store.getters.permissionIsEnabled(action)
    }
  },
  computed: {
    merchant: function () {
      return this.$store.getters.merchant
    },
    account: function () {
      return this.$store.getters.account
    },
    showCancelButton () {
      const status = this.subscription.status.toLowerCase()
      return (
        status !== 'canceled' &&
        status !== 'cancelado' &&
        status !== 'failed' &&
        status !== 'falha'
      )
    },
    params () {
      return {
        merchantId: this.merchant.id,
        accountId: this.account.id,
        subscriptionId: this.$route.params.id
      }
    },
    isTestAccount: function () {
      return this.account.account_type.value === 'test'
    }
  },
  created: function () {
    if (this.$route.params.tab === 'info') {
      this.getSubscription()
    }
  }
}
</script>
